// Reusable horizontal carousel. Renders children in a horizontally
// scrolling track with CSS scroll-snap, plus prev/next buttons that
// scroll the track one "slot" at a time. An optional moreLink
// renders below the track as a "more →" CTA (used by the news +
// insta + videos rows on polls_open to link out to the source site).
//
// Touch + trackpad scrolling work natively (the buttons are the
// keyboard / desktop affordance). The track hides its scrollbar
// for clean visuals; the prev/next buttons disable when the track
// is at its respective edge.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import './Carousel.css';

interface Props {
  /** Each child becomes one carousel slot. */
  children: ReactNode;
  /** Per-slot minimum width (CSS length). Drives roughly how many
   *  slots fit in view at once. Defaults to 280px. */
  itemMin?: string;
  /** Gap between carousel items (CSS length). Defaults to 14px. */
  gap?: string;
  /** Accessible label for the track. */
  ariaLabel: string;
  /** Optional CTA shown beneath the track ("More from Lovin Malta →"). */
  moreLink?: { href: string; label: string };
}

export default function Carousel({
  children,
  itemMin = '280px',
  gap = '14px',
  ariaLabel,
  moreLink,
}: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Update the prev/next disabled state on every scroll + on mount.
  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;
    const update = () => {
      // Tiny epsilon — scrollLeft can be sub-pixel after smooth scroll.
      setAtStart(node.scrollLeft <= 1);
      setAtEnd(node.scrollLeft + node.clientWidth >= node.scrollWidth - 1);
    };
    update();
    node.addEventListener('scroll', update, { passive: true });
    const onResize = () => update();
    window.addEventListener('resize', onResize);
    return () => {
      node.removeEventListener('scroll', update);
      window.removeEventListener('resize', onResize);
    };
  }, [children]);

  function scrollByDirection(dir: -1 | 1) {
    const node = trackRef.current;
    if (!node) return;
    // Scroll by ~85% of the viewport width so each click reveals a
    // fresh set of slots without losing the trailing item to context.
    const dx = Math.round(node.clientWidth * 0.85) * dir;
    node.scrollBy({ left: dx, behavior: 'smooth' });
  }

  return (
    <div className="carousel">
      <div
        ref={trackRef}
        className="carousel__track"
        role="region"
        aria-label={ariaLabel}
        // Inline custom properties drive per-slot min-width + gap
        // via CSS vars, so each caller tunes the carousel without
        // one-off CSS classes.
        style={{
          ['--carousel-item-min' as string]: itemMin,
          ['--carousel-gap' as string]: gap,
        }}
      >
        {/* Wrap each child in a snap-aligned item. React.Children.map
            handles arrays / fragments cleanly. */}
        {Array.isArray(children)
          ? children.map((child, i) => (
              <div key={i} className="carousel__item">
                {child}
              </div>
            ))
          : <div className="carousel__item">{children}</div>}
      </div>

      <div className="carousel__controls">
        <div className="carousel__buttons">
          <button
            type="button"
            className="carousel__btn"
            onClick={() => scrollByDirection(-1)}
            disabled={atStart}
            aria-label="Previous"
            title="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            className="carousel__btn"
            onClick={() => scrollByDirection(1)}
            disabled={atEnd}
            aria-label="Next"
            title="Next"
          >
            ›
          </button>
        </div>
        {moreLink && (
          <a
            href={moreLink.href}
            target="_blank"
            rel="noopener noreferrer"
            className="carousel__more"
          >
            {moreLink.label}
          </a>
        )}
      </div>
    </div>
  );
}
